// eslint-disable-next-line import/prefer-default-export
import PassportImage from '@/assets/images/documents/document-passport.png';
import DriverLicenseImage from '@/assets/images/documents/document-driver-license.png';
import IDImage from '@/assets/images/documents/document-id.png';
import VNRImage from '@/assets/images/documents/document-vnr.jpeg';

export const productTypes = {
  DEFAULT_TYPE: 'products',
  TEST_SPEED_TYPE: 'testSpeed',
};

export const SERVICE_PRODUCT_KEYS = {
  SHIPPING_FEE: 'SHIPPING_FEE',
};

// Handle of the "location" that stands for a sample taken at home instead of at a
// ZIZ branch. Home test methods carry no branch locations, so the checkout falls
// back to this handle when no location was picked.
export const HOME_LOCATION_HANDLE = 'home';

// Product types that never travel as a parcel: the service fees are not goods at
// all, and the e-consult is delivered as a dermatologist's reply online. A cart
// holding only these has nothing to ship, so it is never charged a shipping fee.
export const NON_SHIPPABLE_PRODUCT_TYPES = ['consult', 'service'];

// Keys of the mycoplasma test products (urine + vagina). When one of these is
// in the cart, the "3 uur" result-time option replaces "90 minuten".
export const MYCOPLASMA_PRODUCT_KEYS = [
  'f01d91a0376dadc9fa5ce4b52fe5d029d0b1f06c58cbd0798a3276a39c64963d',
  '9432eba7cd81d05e333cd0b6b5a90f6d8c03b58e7e088ed57f55e47376c8fdcb',
];

// Result-time (testSpeed) product keys that are swapped for mycoplasma carts.
export const TEST_SPEED_KEYS = {
  NINETY_MINUTES: '0de310fe68b8746973f6cf684f4f436773297e95585b6d26a0d3a2be9efe8382',
  THREE_HOURS: '04251dbaed22028b4aa29de608cb744903368cec3fecbca39faad237a05608fe',
};

// Standard result-time options ("Uitslag na 3 uur" / "Uitslag na 90 minuten").
// The faster 30-minute option carries a fee and is deliberately
// excluded so it is never auto-selected (ZIZ-1252).
export const STANDARD_TEST_SPEED_KEYS = [
  TEST_SPEED_KEYS.THREE_HOURS,
  TEST_SPEED_KEYS.NINETY_MINUTES,
];

export const riskTypes = {
  HIGH: 'high',
  LOW: 'low',
  MID: 'mid',
};

// properties 0, 1, 2 represent risk types
export const riskTypesList = {
  0: 'mid',
  1: 'high',
  2: 'low',
};

export const riskTypesNumberList = {
  mid: 0,
  high: 1,
  low: 2,
};

// properties 0, 1, 2 represent risk types
export const riskColors = {
  0: '#FCF5E9',
  1: '#FEEEEA',
  2: '#E6F8F1',
};

export const identityTypes = {
  PASSPORT: 'passport',
  DRIVING: 'driving',
  ID: 'id',
  VNR: 'vnr',
};

export const consultTypes = {
  GO_TO_BRANCH: 'go-to-branch',
  DELIVER_HOME: 'deliver-home',
  MICROSOFT: 'microsoft',
};

const translatedText = {
  nl: {
    checkoutPage: {
      redirectTitle: 'Door naar betaling',
      redirectLink: 'Open betaling',
      paymentErrorTitle: 'Betaling niet gestart',
      paymentErrorText: 'Probeer het opnieuw.',
      paymentMethodTitle: 'Betaalwijze',
      payOrder: 'Bestelling betalen',
      cardPaymentTitle: 'Kaartbetaling',
      amount: 'Bedrag:',
      total: 'Totaal',
      confirmPayment: 'Bevestigen',
      retry: 'Probeer opnieuw',
      cardFormInvalid: 'Controleer de gegevens van je kaart.',
      methodsUnavailable: 'De betaalmethoden konden niet worden geladen. Controleer je internetverbinding.',
      cardFormUnavailable: 'Het betaalformulier kon niet worden geladen. Kies iDEAL of probeer het opnieuw.',
    },
    // Method labels are keyed on the MSP gateway code, not on the backend `label`:
    // GET /payment/methods returns fixed Dutch labels for every locale.
    paymentMethods: {
      IDEAL: 'iDEAL | Wero',
      CREDITCARD: 'Creditcard',
    },
    documents: {
      passport: {
        label: 'Paspoort',
        modalLinkText: 'Waar staat mijn documentnummer?',
        modalTitle: 'Waar staat mijn\ndocumentnummer?',
        modalText: "Uw paspoortnummer bestaat uit negen tekens. Het bevindt zich rechts van je naam onder het kopje 'documentnummer'.",
      },
      driving: {
        label: 'Rijbewijs',
        modalLinkText: 'Waar staat mijn rijbewijsnummer?',
        modalTitle: 'Waar staat mijn\nrijbewijsnummer?',
        modalText: 'Een rijbewijsnummer bestaat uit tien cijfers. Let op: een rijbewijsnummer is anders dan uw BSN-nummer.',
      },
      id: {
        label: 'ID-kaart',
        modalLinkText: 'Waar staat mijn documentnummer?',
        modalTitle: 'Waar staat mijn\ndocumentnummer?',
        modalText: "Het documentnummer op een ID-kaart bestaat uit negen tekens. Het bevindt zich rechts van je naam onder het kopje 'documentnummer'.",
      },
      vnr: {
        label: 'Verblijfsvergunning',
        modalLinkText: 'Waar staat mijn documentnummer?',
        modalTitle: 'Waar staat mijn\ndocumentnummer?',
        modalText: 'Onderaan de verblijfsvergunning staan de 10 cijfers van uw V-nummer. Dat nummer staat achter de letters VNR.',
      },
    },
    documentType: {
      fillDocumentNumber: '1. Vul je documentnummer in',
      documentNumber: '1. Documentnummer',
      documentNumberDescription: 'Vul een documentnummer in van je paspoort, rijbewijs of ID-kaart.',
    },
    diseaseRisk: {
      testAdvice: 'Jouw testadvies',
      emergencyPrefix: 'Misschien is',
      emergencyStrong: 'Spoedbehandeling',
      emergencySuffix: 'mogelijk of nodig',
      noRisksPrefix: 'We vonden bij jou',
      noRisksStrong: 'geen verhoogd risico',
      noRisksSuffix: 'overweeg bij twijfel te testen op',
      riskSingular: 'risico',
      riskPlural: 'risico\'s',
      chance: 'kans',
      prepLabel: 'Hoort bij PrEP',
      moreInfo: 'Meer info',
      moreInformation: 'Meer informatie',
      moreInfoQuestion: 'Waar wil je meer<br />informatie over?',
      disabledText: 'Nog niet mogelijk in dit geval',
      secureConnectionText: 'Beveiligde verbinding',
    },
    resultProduct: {
      add: 'Toevoegen',
      moreInfo: 'Meer info',
      moreInformation: 'Meer informatie',
      moreInfoQuestion: 'Waar wil je meer<br />informatie over?',
    },
    radioAddToCart: {
      noOptionForSelection: 'Geen optie bij deze selectie',
    },
    tripettoForm: {
      loading: 'Vragenlijst wordt geladen…',
    },
    photoConsult: {
      idNotFound: 'ID niet gevonden',
      success: 'Uw foto’s zijn succesvol ontvangen.',
      intro: 'Bij het foto consult stuur je 2 foto’s op. Deze worden anoniem beoordeeld door een dermatoloog.',
      instructions: 'Maak twee duidelijke foto’s van dichtbij.',
      light: 'Zorg hierbij voor voldoende licht.',
      warningLabel: 'Let op:',
      warningPrefix: 'Zorg dat je',
      warningStrong: 'niet herkenbaar in beeld bent',
      error: 'Er is een fout opgetreden. Probeer het opnieuw.',
      sending: 'Verzenden...',
      submit: 'Verzenden',
      photo1: 'Foto 1',
      photo2: 'Foto 2',
      fileTooLarge: 'Bestand mag niet groter zijn dan 10 MB.',
      invalidFileType: 'Ongeldig bestandstype.',
      required: 'Dit veld is verplicht.',
      uploadHint: 'Sleep of klik om te uploaden',
      uploadFormats: 'JPG, PNG, TIFF, PDF · max 10 MB',
      delete: 'Delete',
    },
    checkoutStatus: {
      paymentSuccess: 'Betaling geslaagd!',
      completeLastSteps: 'Voltooi de laatste stappen.',
      nextWorkingDay: 'Voor 16:30 besteld, volgende werkdag in huis',
      safeAndDiscreet: 'Veilig en discreet',
      driverLicense: 'Rijbewijs',
      identityCard: 'ID-kaart',
      residencePermit: 'verblijfsvergunning',
      documentNumber: 'Documentnummer',
      documentRequired: 'Dit is verplicht voor de zorgverzekeringswet.',
      skipForNow: 'Ik geef dit later door',
      enterDocumentNumber: 'Voer het documentnummer van je {document} in.',
      continue: 'Ga verder',
      change: 'Wijzigen',
      intake: 'Intake',
      intakeDescription: 'Deze informatie is belangrijk als je behandeling nodig hebt. Invullen duurt minder dan één minuut.',
      startIntake: 'Start intake',
      paymentConfirming: 'Betaling wordt bevestigd...',
      thisMayTakeMoment: 'Dit kan even duren. De pagina wordt automatisch bijgewerkt zodra uw betaling is bevestigd.',
      paymentFailed: 'Betaling niet gelukt!',
      paymentFailedText: 'Er is niets in rekening gebracht. Probeer opnieuw te betalen of ga terug naar je resultaten.',
      paymentVerifying: 'Je betaling wordt gecontroleerd',
      paymentVerifyingText: 'Je betaling wordt op dit moment om veiligheidsredenen gecontroleerd. Dit kan soms langer duren dan gebruikelijk. Doe geen tweede betaling. Zodra de controle klaar is, verwerken wij je bestelling. Je ontvangt een e-mail zodra je betaling bevestigd is.',
      backToResults: 'Terug naar resultaat',
      tryAgain: 'Nog een keer proberen',
    },
    button: {
      moreInformation: 'Meer informatie',
    },
  },
  en: {
    checkoutPage: {
      redirectTitle: 'Redirecting to payment',
      redirectLink: 'Open payment',
      paymentErrorTitle: 'Payment not started',
      paymentErrorText: 'Please try again.',
      paymentMethodTitle: 'Payment method',
      payOrder: 'Pay order',
      cardPaymentTitle: 'Card payment',
      amount: 'Amount:',
      total: 'Total',
      confirmPayment: 'Confirm',
      retry: 'Try again',
      cardFormInvalid: 'Please check your card details.',
      methodsUnavailable: 'The payment methods could not be loaded. Please check your internet connection.',
      cardFormUnavailable: 'The payment form could not be loaded. Choose iDEAL or try again.',
    },
    paymentMethods: {
      IDEAL: 'iDEAL | Wero',
      CREDITCARD: 'Credit card',
    },
    documents: {
      passport: {
        label: 'Passport',
        modalLinkText: 'Where can I find my document number?',
        modalTitle: 'Where can I find my\ndocument number?',
        modalText: "Your passport number consists of nine characters. It is located to the right of your name under the heading 'document number'.",
      },
      driving: {
        label: 'Driver’s license',
        modalLinkText: 'Where can I find my driver’s license number?',
        modalTitle: 'Where can I find my\ndriver’s license number?',
        modalText: 'A driver’s license number consists of ten digits. Note: a driver’s license number is different from your BSN number.',
      },
      id: {
        label: 'ID card',
        modalLinkText: 'Where can I find my document number?',
        modalTitle: 'Where can I find my\ndocument number?',
        modalText: "The document number on an ID card consists of nine characters. It is located to the right of your name under the heading 'document number'.",
      },
      vnr: {
        label: 'Residence permit',
        modalLinkText: 'Where can I find my document number?',
        modalTitle: 'Where can I find my\ndocument number?',
        modalText: 'The 10 digits of your V-number are at the bottom of the residence permit. The number is shown after the letters VNR.',
      },
    },
    documentType: {
      fillDocumentNumber: '1. Enter your document number',
      documentNumber: '1. Document number',
      documentNumberDescription: 'Enter a document number from your passport, driver’s license, or ID card.',
    },
    diseaseRisk: {
      testAdvice: 'Your test advice',
      emergencyPrefix: 'Maybe',
      emergencyStrong: 'emergency treatment',
      emergencySuffix: 'is possible or needed',
      noRisksPrefix: 'We found',
      noRisksStrong: 'no increased risk',
      noRisksSuffix: 'consider testing for',
      riskSingular: 'risk',
      riskPlural: 'risks',
      chance: 'chance',
      prepLabel: 'Part of PrEP',
      moreInfo: 'More info',
      moreInformation: 'More information',
      moreInfoQuestion: 'What would you like<br />more information about?',
      disabledText: 'Not possible in this case yet',
      secureConnectionText: 'Secure connection',
    },
    resultProduct: {
      add: 'Add',
      moreInfo: 'More info',
      moreInformation: 'More information',
      moreInfoQuestion: 'What would you like<br />more information about?',
    },
    radioAddToCart: {
      noOptionForSelection: 'No option for this selection',
    },
    tripettoForm: {
      loading: 'Loading the questionnaire…',
    },
    photoConsult: {
      idNotFound: 'ID not found',
      success: 'Your photos have been received successfully.',
      intro: 'For the photo consultation, send 2 photos. They will be reviewed anonymously by a dermatologist.',
      instructions: 'Take two clear close-up photos.',
      light: 'Make sure there is enough light.',
      warningLabel: 'Note:',
      warningPrefix: 'Make sure you are',
      warningStrong: 'not recognizable in the image',
      error: 'An error occurred. Please try again.',
      sending: 'Sending...',
      submit: 'Send',
      photo1: 'Photo 1',
      photo2: 'Photo 2',
      fileTooLarge: 'File may not be larger than 10 MB.',
      invalidFileType: 'Invalid file type.',
      required: 'This field is required.',
      uploadHint: 'Drag or click to upload',
      uploadFormats: 'JPG, PNG, TIFF, PDF · max 10 MB',
      delete: 'Delete',
    },
    checkoutStatus: {
      paymentSuccess: 'Payment successful!',
      completeLastSteps: 'Complete the final steps.',
      nextWorkingDay: 'Ordered before 16:30, delivered the next working day',
      safeAndDiscreet: 'Safe and discreet',
      driverLicense: 'Driver’s license',
      identityCard: 'ID card',
      residencePermit: 'residence permit',
      documentNumber: 'Document number',
      documentRequired: 'This is required under the Health Insurance Act.',
      skipForNow: 'I will provide this later',
      enterDocumentNumber: 'Enter the document number from your {document}.',
      continue: 'Continue',
      change: 'Change',
      intake: 'Intake',
      intakeDescription: 'This information is important if you need treatment. It takes less than one minute to complete.',
      startIntake: 'Start intake',
      paymentConfirming: 'Payment is being confirmed...',
      thisMayTakeMoment: 'This may take a moment. The page will update automatically when your payment is confirmed.',
      paymentFailed: 'Payment unsuccessful!',
      paymentFailedText: 'No charge was made. Try paying again, or go back to your results.',
      paymentVerifying: 'Your payment is being verified',
      paymentVerifyingText: 'Your payment is currently being checked for security reasons. This can sometimes take longer than usual. Please do not make another payment. We will process your order once the verification is complete. You will receive an email as soon as your payment is confirmed.',
      backToResults: 'Back to results',
      tryAgain: 'Try again',
    },
    button: {
      moreInformation: 'More information',
    },
  },
};

export const getTranslatedText = (key, locale = 'nl') => translatedText[locale]?.[key]
  || translatedText.nl[key]
  || {};

const getDocumentText = (type, locale) => getTranslatedText('documents', locale)[type];

// eslint-disable-next-line import/prefer-default-export
export const AVAILABLE_DOCUMENTS = [{
  id: 1,
  label: getDocumentText('passport', 'nl').label,
  type: identityTypes.PASSPORT,
  value: 1,
  icon: 'passport',
  input: {
    label: '',
    placeholder: 'AA12345678',
    modalLinkText: getDocumentText('passport', 'nl').modalLinkText,
  },
  modal: {
    title: getDocumentText('passport', 'nl').modalTitle,
    text: getDocumentText('passport', 'nl').modalText,
    img: PassportImage,
  },
}, {
  id: 2,
  label: getDocumentText('driving', 'nl').label,
  type: identityTypes.DRIVING,
  value: 2,
  icon: 'driver-license',
  input: {
    label: '',
    placeholder: '1234567890',
    modalLinkText: getDocumentText('driving', 'nl').modalLinkText,
    mask: '##########',
  },
  modal: {
    title: getDocumentText('driving', 'nl').modalTitle,
    text: getDocumentText('driving', 'nl').modalText,
    img: DriverLicenseImage,
  },
}, {
  id: 3,
  label: getDocumentText('id', 'nl').label,
  type: identityTypes.ID,
  value: 3,
  icon: 'id',
  input: {
    label: '',
    placeholder: 'AA1234567',
    modalLinkText: getDocumentText('id', 'nl').modalLinkText,
  },
  modal: {
    title: getDocumentText('id', 'nl').modalTitle,
    text: getDocumentText('id', 'nl').modalText,
    img: IDImage,
  },
}, {
  id: 4,
  label: getDocumentText('vnr', 'nl').label,
  type: identityTypes.VNR,
  value: 4,
  icon: 'vnr',
  input: {
    label: '',
    placeholder: '1234567890',
    modalLinkText: getDocumentText('vnr', 'nl').modalLinkText,
    mask: '##########',
  },
  modal: {
    title: getDocumentText('vnr', 'nl').modalTitle,
    text: getDocumentText('vnr', 'nl').modalText,
    img: VNRImage,
  },
}];

export const getAvailableDocuments = (locale = 'nl') => {
  const documentsText = getTranslatedText('documents', locale);
  const textByType = {
    [identityTypes.PASSPORT]: documentsText.passport,
    [identityTypes.DRIVING]: documentsText.driving,
    [identityTypes.ID]: documentsText.id,
    [identityTypes.VNR]: documentsText.vnr,
  };

  return AVAILABLE_DOCUMENTS.map((document) => {
    const text = textByType[document.type];

    return {
      ...document,
      label: text.label,
      input: {
        ...document.input,
        modalLinkText: text.modalLinkText,
      },
      modal: {
        ...document.modal,
        title: text.modalTitle,
        text: text.modalText,
      },
    };
  });
};
